// src/features/pages/admin/roles/RoleBuilderDialog.tsx
// Create/edit a tenant custom role: name, key (immutable after create), scope
// (immutable after create), description, and a searchable permission picker
// restricted to the tenant's ALLOWED catalog.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  createRole,
  updateRole,
  type RbacCatalog,
  type Role,
  type RoleScope,
} from '@/services/rbacService';
import { PermissionPicker } from './PermissionPicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: RbacCatalog;
  /** Present = edit mode; absent = create mode. */
  role?: Role | null;
  onSaved: () => void;
}

// Org-scope redesign Step 5: DIRECT_REPORTS (immediate reports) is the recommended
// default for management/executive roles; Team (recursive descendants) and Tenant
// (company-wide) are explicit opt-ins that carry a guardrail warning.
const SCOPES: { value: RoleScope; label: string; hint: string; warn?: string }[] = [
  { value: 'self', label: 'Self', hint: 'Acts on their own records only' },
  { value: 'direct_reports', label: 'Direct reports', hint: 'Immediate reports only (recommended for managers/executives)' },
  {
    value: 'team',
    label: 'Team (all reports)',
    hint: 'Their ENTIRE reporting tree, every level down',
    warn: 'Team scope grants visibility over the whole descendant tree — every level below this person, not just their immediate reports. Use Direct reports unless the wider reach is intended.',
  },
  {
    value: 'tenant',
    label: 'Tenant',
    hint: 'Every employee in the company',
    warn: 'Tenant scope grants company-wide operational visibility of every employee. Reserve it for HR / finance / audit roles — an executive who only needs company numbers should use Direct reports plus the executive.analytics.read permission instead.',
  },
];

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const KEY_RE = /^[a-z][a-z0-9_]*$/;

const inputCls =
  'mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400';

export const RoleBuilderDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  catalog,
  role,
  onSaved,
}) => {
  const isEdit = !!role;
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState('');
  // Conservative default: immediate reports, not company-wide (Step 5 guardrail).
  const [scope, setScope] = useState<RoleScope>('direct_reports');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Reset the form whenever the dialog opens (for a fresh create or a specific edit).
  useEffect(() => {
    if (!open) return;
    setLabel(role?.label ?? '');
    setKey(role?.key ?? '');
    setKeyTouched(false);
    setDescription(role?.description ?? '');
    setScope((role?.scope as RoleScope) ?? 'direct_reports');
    setSelected(new Set(role?.permissions ?? []));
    setSaving(false);
  }, [open, role]);

  // Auto-derive the key from the label until the user edits the key directly.
  useEffect(() => {
    if (!isEdit && !keyTouched) setKey(slugify(label));
  }, [label, isEdit, keyTouched]);

  const keyValid = isEdit || (KEY_RE.test(key) && key.length >= 2);
  const canSave = label.trim().length >= 2 && keyValid && selected.size > 0 && !saving;

  const summary = useMemo(() => {
    if (selected.size === 0) return 'No permissions selected yet.';
    const byService = new Map<string, number>();
    for (const k of selected) {
      const svc = k.includes(':') ? k.slice(0, k.indexOf(':')) : 'other';
      byService.set(svc, (byService.get(svc) ?? 0) + 1);
    }
    const parts = [...byService.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([svc, n]) => `${n} in ${svc.replace(/[-_]/g, ' ')}`);
    const extra = byService.size > 4 ? ` +${byService.size - 4} more areas` : '';
    return `${selected.size} permission${selected.size === 1 ? '' : 's'} — ${parts.join(', ')}${extra}`;
  }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit && role) {
        await updateRole(role.id, {
          label: label.trim(),
          description: description.trim() || undefined,
          permissions: [...selected],
        });
        toast.success(`Role “${label.trim()}” updated`);
      } else {
        await createRole({
          key,
          label: label.trim(),
          description: description.trim() || undefined,
          scope,
          permissions: [...selected],
        });
        toast.success(`Role “${label.trim()}” created`);
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      // Surface the backend's business message verbatim (e.g. "cannot grant a
      // permission you do not hold" / "permission not permitted for your
      // organization: <key>").
      toast.error(getApiErrorMessage(e, 'Could not save the role'), { duration: 7000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* grid-rows pins the header + footer and lets only the middle body
          scroll, so the Create/Save actions stay visible on short screens. */}
      <DialogContent className="max-w-2xl grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit role' : 'Create custom role'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this role’s name, description and permissions. The key and scope are fixed.'
              : 'Define a role for your company by picking permissions from your allowed catalog.'}
          </DialogDescription>
        </DialogHeader>

        {/* The single scroll region for this dialog (min-h-0 lets the grid row
            shrink so this can scroll instead of pushing the footer off-screen).
            -mx/px keeps focus rings from being clipped by overflow. */}
        <div className="space-y-4 overflow-y-auto min-h-0 -mx-1 px-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="role-label">
                Role name
              </label>
              <input
                id="role-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Regional HR Lead"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="role-key">
                Key {isEdit && <span className="text-muted-foreground">(fixed)</span>}
              </label>
              <input
                id="role-key"
                value={key}
                disabled={isEdit}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toLowerCase());
                }}
                placeholder="regional_hr_lead"
                className={`${inputCls} font-mono disabled:opacity-60`}
              />
              {!isEdit && key && !keyValid && (
                <p className="mt-1 text-xs text-danger-strong">
                  Use lowercase letters, numbers and underscores (start with a letter).
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground" htmlFor="role-desc">
              Description <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this role is for"
              className={inputCls}
            />
          </div>

          <div>
            <span className="text-sm font-medium text-foreground">Scope</span>
            {isEdit ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {SCOPES.find((s) => s.value === scope)?.label ?? scope} —{' '}
                {SCOPES.find((s) => s.value === scope)?.hint} (fixed)
              </p>
            ) : (
              <>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {SCOPES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setScope(s.value)}
                      className={`text-left rounded-[10px] border px-3 py-2 transition-colors ${
                        scope === s.value
                          ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/10'
                          : 'border-border hover:border-orange-200'
                      }`}
                    >
                      <span className="block text-sm font-semibold text-foreground">{s.label}</span>
                      <span className="block text-xs text-muted-foreground">{s.hint}</span>
                    </button>
                  ))}
                </div>
                {SCOPES.find((s) => s.value === scope)?.warn && (
                  <div className="mt-2 flex items-start gap-2 rounded-[10px] border border-amber-300/60 bg-amber-50/60 dark:bg-amber-500/[0.08] px-3 py-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span className="text-xs text-amber-800 dark:text-amber-300">
                      {SCOPES.find((s) => s.value === scope)?.warn}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <span className="text-sm font-medium text-foreground">Permissions</span>
            <div className="mt-2">
              <PermissionPicker catalog={catalog} selected={selected} onChange={setSelected} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">This role can: </span>
              {summary}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleBuilderDialog;
